import { ArrowRight, Building2, Download, ShoppingBag, Terminal } from 'lucide-react';

import { Band, ctaClasses, Medallion, panelClasses, SectionTitle } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero, Reveal } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { ONE_PAGERS, ONE_PAGER_ORDER } from './one-pager-content';

import type { OnePagerSlug } from './one-pager-content';
import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/** The bedrock card, spread onto the locale-aware router `<Link>`. */
const CARD_CLASS = panelClasses({
  interactive: true,
  className: 'flex h-full flex-col p-6',
});

/** One mark per archetype — the market it sells into, at a glance. */
const ARCHETYPE_MARK: Record<OnePagerSlug, { icon: LucideIcon; hue: MedallionHue }> = {
  'hebrew-b2c': { icon: ShoppingBag, hue: 'cyan' },
  'b2b-saas-docs-heavy': { icon: Terminal, hue: 'blue' },
  'niche-enterprise': { icon: Building2, hue: 'violet' },
};

/**
 * AF-138 — `/one-pagers` index. The three ICP-archetype sheets.
 *
 * ## Three defects this page carried, all of them visible on arrival
 *
 *  - **It ended in a 360px void.** Three cards, then nothing, then the footer.
 *    A page whose whole purpose is sales enablement stopped without saying what
 *    to do with what it had just shown you.
 *  - **The archetypes were out of their own order** — B, A, C — because the SOT
 *    array happened to list the B2B sheet first. The labels say A/B/C, so the
 *    order is not a preference, and reading them out of sequence is the kind of
 *    detail that costs a page its credibility with the exact reader it is for.
 *  - **The eyebrow wrapped on two of three cards**, so the three titles started
 *    at three different heights in one row. `min-h` on the label reserves the
 *    second line for all three, which is what makes them line up in the longest
 *    locale as well as in English (German runs these labels ~30% longer).
 *
 * The closing band solves the first two problems at once: the built PDFs
 * (`public/one-pagers/<slug>.pdf`, generated at build time) were reachable only
 * from inside each sheet, so the index of a sales asset never offered the
 * asset. Putting them at the end gives the page a reason to end.
 */
export function OnePagersIndex(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('onepagers.index.metaTitle'),
    description: t('onepagers.index.metaDescription'),
    path: '/one-pagers',
  });
  return (
    <main
      id="main"
      tabIndex={-1}
      className="min-h-screen bg-[color:var(--bd-page)] text-base leading-relaxed"
    >
      <PageHero title={t('onepagers.index.title')} description={t('onepagers.index.intro')} />

      <Container width="default" className="py-12 sm:py-16">
        {/* Three archetypes, so the two-column grid left a hole in the
            bottom-right at every desktop width. Three tracks at `lg` seats
            them in one clean row. */}
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ONE_PAGER_ORDER.map((slug, i) => {
            const data = ONE_PAGERS[slug];
            const mark = ARCHETYPE_MARK[slug];
            return (
              <Reveal as="li" key={slug} delayMs={Math.min(i, 4) * 60}>
                <Link to={`/one-pagers/${slug}`} className={CARD_CLASS}>
                  <Medallion icon={mark.icon} hue={mark.hue} />
                  {/* `min-h` reserves the second line on every card, so a label
                      that wraps in one locale does not push one title out of
                      line with its two siblings. */}
                  <p className="mb-1 mt-5 min-h-[2.4em] font-mono rtl:font-sans text-[10.5px] uppercase leading-[1.2] tracking-[0.13em] text-[color:var(--bd-muted)] rtl:normal-case rtl:tracking-normal">
                    {t(data.archetypeKey)}
                  </p>
                  <h2 className="mb-2 text-xl font-semibold tracking-tight">{t(data.titleKey)}</h2>
                  <p className="text-sm text-[color:var(--bd-muted)]">{t(data.summaryKey)}</p>
                  {/* `mt-auto` pins the affordance to the bottom of whichever
                      card in the row is tallest, so the three line up instead
                      of floating at three heights set by summary length. */}
                  <span
                    aria-hidden="true"
                    className="mt-auto inline-flex items-center gap-1.5 pt-5 text-[0.82rem] font-medium text-[color:var(--bd-link)]"
                  >
                    {t('onepagers.index.readSheet')}
                    <ArrowRight className="size-3.5 rtl:rotate-180" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </ul>
      </Container>

      <Band as="section" tone="band" space="sm" aria-labelledby="one-pager-pdfs">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-center">
          <div>
            <SectionTitle onDark id="one-pager-pdfs" className="text-[clamp(1.4rem,2.4vw,1.9rem)]">
              {t('onepagers.index.pdfHeading')}
            </SectionTitle>
            <p className="mt-3 max-w-md text-pretty text-[0.95rem] leading-[1.66] text-[color:var(--bd-band-body)]">
              {t('onepagers.index.pdfBody')}
            </p>
          </div>
          <ul className="grid gap-2.5">
            {ONE_PAGER_ORDER.map((slug) => (
              <li key={slug}>
                {/* A real file download, so a plain `<a download>` rather than a
                    router link — the PDFs are static build artifacts served from
                    the same origin. */}
                <a
                  href={`/one-pagers/${slug}.pdf`}
                  download
                  className={ctaClasses({
                    variant: 'secondary',
                    onDark: true,
                    size: 'sm',
                    className: 'w-full justify-between text-start',
                  })}
                >
                  {t(ONE_PAGERS[slug].titleKey)}
                  <Download aria-hidden="true" className="size-4 flex-none" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </Band>
    </main>
  );
}
